import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { patchPolicyDraft, emergencyPublishPolicy, getPolicyTestReport } from "@/lib/api";
import { errMsg, errList, fmtValue, fmtDate, isCondSchema, SEL, Stat, Empty } from "./policyShared";

const types = (s) => (Array.isArray(s?.type) ? s.type : [s?.type]);
const hasSimpleEditor = (s) => {
  const t = types(s);
  return t.includes("boolean") || t.includes("number") || t.includes("integer") || (s?.enum || []).some((v) => v !== null) || (t.includes("string") && !isCondSchema(s));
};

function ValueEditor({ schema, value, onChange }) {
  const t = types(schema);
  const enumVals = (schema?.enum || []).filter((v) => v !== null);
  if (t.includes("boolean")) {
    return (
      <select className={`${SEL} w-full`} value={value === null || value === undefined ? "" : String(value)} onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === "true")} data-testid="policy-change-value">
        <option value="">— blank —</option><option value="true">Yes</option><option value="false">No</option>
      </select>
    );
  }
  if (enumVals.length) {
    return (
      <select className={`${SEL} w-full`} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)} data-testid="policy-change-value">
        <option value="">— blank —</option>{enumVals.map((v) => <option key={v} value={v}>{v}</option>)}
      </select>
    );
  }
  if (t.includes("number") || t.includes("integer")) {
    return <Input type="number" step="any" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} data-testid="policy-change-value" />;
  }
  if (t.includes("string") && !isCondSchema(schema)) {
    return <Input value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)} data-testid="policy-change-value" />;
  }
  return null;
}

// Propose one change on a draft: edit a setting, add a table row ([+]) or remove a row (value null).
export function ChangeDialog({ change, draftId, onClose, onApplied }) {
  const [path, setPath] = useState("");
  const [value, setValue] = useState(null);
  const [json, setJson] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const open = !!change;
  const simple = change && change.mode !== "remove" && hasSimpleEditor(change.schema);
  useEffect(() => {
    if (!change) return;
    setPath(change.path); setValue(change.value ?? null); setJson(JSON.stringify(change.value ?? null, null, 2)); setReason(""); setErrors([]);
  }, [change]);
  if (!change) return null;

  const submit = async () => {
    let v = value;
    if (change.mode === "remove") v = null;
    else if (!simple) {
      try { v = json.trim() === "" ? null : JSON.parse(json); } catch (e) { setErrors([{ path, error: `Value is not valid JSON: ${e.message}` }]); return; }
    }
    if (!reason.trim()) { setErrors([{ path, error: "A reason is required for every change" }]); return; }
    setBusy(true); setErrors([]);
    try {
      const res = await patchPolicyDraft(draftId, [{ path, value: v, reason }]);
      toast.success(`Change applied — ${res.diff_against_live?.length ?? 0} setting(s) now differ from Live`);
      onApplied?.(res); onClose();
    } catch (e) {
      setErrors(errList(e).length ? errList(e) : [{ path, error: errMsg(e) }]);
    } finally { setBusy(false); }
  };

  const title = change.mode === "add" ? "Add a row" : change.mode === "remove" ? "Remove this row" : "Propose a change";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="policy-change-dialog">
        <DialogHeader>
          <DialogTitle>{title}{change.schema?.title ? ` — ${change.schema.title}` : ""}</DialogTitle>
          <DialogDescription>{change.schema?.description || "Every change needs a reason; it is recorded in the change log and shown to the approver."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500">Setting path</label>
            <Input value={path} onChange={(e) => setPath(e.target.value)} className="font-mono text-xs" data-testid="policy-change-path" />
          </div>
          {change.mode === "remove" ? (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2" data-testid="policy-change-remove-note">This row will be removed from the draft: <code className="text-xs">{fmtValue(change.value)}</code></div>
          ) : (
            <div>
              <label className="text-xs text-slate-500">New value{change.mode === "edit" && <span className="text-slate-400"> (currently: <span className="font-mono">{fmtValue(change.value, change.schema?.["x-unit"])}</span>)</span>}</label>
              {simple ? <ValueEditor schema={change.schema} value={value} onChange={setValue} />
                : <Textarea value={json} onChange={(e) => setJson(e.target.value)} rows={10} className="font-mono text-xs" data-testid="policy-change-json" />}
              {!simple && <div className="text-[11px] text-slate-400 mt-1">JSON — {isCondSchema(change.schema) ? 'a data test like {"field": "inputs.company.age_months", "operator": "<", "value": 12} or {"all": [...]}' : "fill the fields of this row; leave unknowns as null"}</div>}
            </div>
          )}
          <div>
            <label className="text-xs text-slate-500">Reason (required)</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Why this changes — e.g. procedure Part 3 step 2 raised the Tier A minimum" data-testid="policy-change-reason" />
          </div>
          {errors.length > 0 && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2 space-y-0.5" data-testid="policy-change-errors">
              {errors.map((er, i) => <div key={i}>{er.path ? <code>{er.path}</code> : null} {er.error || er.message || JSON.stringify(er)}</div>)}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="policy-change-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="policy-change-submit">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Apply to draft</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Generic confirm-with-text dialog used for approve / send back / publish / rollback / new draft.
export function PromptDialog({ open, onClose, title, description, label = "Comment", required = false, withBump = false, confirmLabel = "Confirm", tone = "bg-emerald-600 hover:bg-emerald-700", onConfirm, testId = "policy-prompt" }) {
  const [text, setText] = useState("");
  const [bump, setBump] = useState("minor");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setText(""); setBump("minor"); } }, [open]);
  const go = async () => {
    if (required && !text.trim()) { toast.error(`${label} is required`); return; }
    setBusy(true);
    try { await onConfirm(text, bump); onClose(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`${testId}-dialog`}>
        <DialogHeader><DialogTitle>{title}</DialogTitle>{description && <DialogDescription>{description}</DialogDescription>}</DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500">{label}{required ? " (required)" : ""}</label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid={`${testId}-text`} />
          </div>
          {withBump && (
            <div>
              <label className="text-xs text-slate-500">Version bump</label>
              <select className={`${SEL} w-full`} value={bump} onChange={(e) => setBump(e.target.value)} data-testid={`${testId}-bump`}>
                <option value="minor">Minor (v x.y+1) — threshold / table edits</option>
                <option value="major">Major (v x+1.0) — new procedure version; all required settings must be filled</option>
              </select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid={`${testId}-cancel`}>Cancel</Button>
          <Button onClick={go} disabled={busy} className={tone} data-testid={`${testId}-confirm`}>{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMERGENCY_TEMPLATE = JSON.stringify([{ path: "product.loan_max_first_time", value: 100000, reason: "Why this must change today" }], null, 2);

export function EmergencyDialog({ open, onClose, onDone }) {
  const [changes, setChanges] = useState(EMERGENCY_TEMPLATE);
  const [reason, setReason] = useState("");
  const [bump, setBump] = useState("minor");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    let parsed;
    try { parsed = JSON.parse(changes); if (!Array.isArray(parsed)) throw new Error("must be a JSON array"); } catch (e) { toast.error(`Changes: ${e.message}`); return; }
    if (!reason.trim()) { toast.error("An emergency publish needs a reason"); return; }
    setBusy(true);
    try {
      const res = await emergencyPublishPolicy({ changes: parsed, reason, version_bump: bump });
      toast.success(`Published ${res.version_label} (emergency)`); onDone?.(res); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="policy-emergency-dialog">
        <DialogHeader>
          <DialogTitle className="text-rose-700">Emergency publish</DialogTitle>
          <DialogDescription>Skips the two-person rule: a draft is created from Live, the changes applied, the test set run and the result published immediately under your name. Use only when a rule must change today.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs text-slate-500">Changes — JSON array of {"{path, value, reason}"}</label><Textarea value={changes} onChange={(e) => setChanges(e.target.value)} rows={8} className="font-mono text-xs" data-testid="policy-emergency-changes" /></div>
          <div><label className="text-xs text-slate-500">Reason (required)</label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="policy-emergency-reason" /></div>
          <div><label className="text-xs text-slate-500">Version bump</label><select className={`${SEL} w-full`} value={bump} onChange={(e) => setBump(e.target.value)} data-testid="policy-emergency-bump"><option value="minor">Minor</option><option value="major">Major</option></select></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={go} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="policy-emergency-confirm">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Publish now</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Sig({ s }) {
  if (!s) return "—";
  return <span className="font-mono text-[11px]">{s.tier || "no tier"} · {s.verdict || "—"} · {fmtValue(s.calculated_offer, "£")}{s.consolidation_eligible ? ` · consolidation ${fmtValue(s.offer_if_consolidated, "£")}` : ""}{s.stops?.length ? ` · stops: ${s.stops.join(", ")}` : ""}{s.flags?.length ? ` · flags: ${s.flags.join(", ")}` : ""}</span>;
}

function CaseResults({ cases }) {
  if (!cases?.length) return null;
  return (
    <div className="overflow-auto rounded-md border border-slate-200 max-h-[300px]" data-testid="policy-report-cases-table">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]"><tr><th className="text-left p-2">Case</th><th className="text-left p-2">Result</th><th className="text-left p-2">Under draft</th><th className="text-left p-2">Mismatches (expected → actual)</th></tr></thead>
        <tbody>
          {cases.map((c) => {
            const bad = Object.entries(c.checks || {}).filter(([, v]) => !v.ok);
            return (
              <tr key={c.id} className="border-t border-slate-100 align-top" data-testid={`policy-report-case-${c.id}`}>
                <td className="p-2"><div className="font-medium">{c.name || c.id}</div><div className="text-slate-400">{c.sf || "synthetic"} · {c.id}</div></td>
                <td className="p-2">{c.passed ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">pass</Badge> : <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100">fail</Badge>}</td>
                <td className="p-2"><Sig s={c.after} /></td>
                <td className="p-2 font-mono text-[11px]">{bad.length ? bad.map(([k, v]) => <div key={k}>{k}: {JSON.stringify(v.expected)} → {JSON.stringify(v.actual)}</div>) : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Opening this dialog records the viewer on the report — approval requires the approver to have seen it.
export function TestReportDialog({ draftId, open, onClose, initial }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!open || !draftId) return;
    setData(initial ? { test_report: initial, stale: false } : null); setErr("");
    getPolicyTestReport(draftId).then(setData).catch((e) => setErr(errMsg(e)));
  }, [open, draftId]); // eslint-disable-line react-hooks/exhaustive-deps
  const r = data?.test_report;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="policy-test-report-dialog">
        <DialogHeader>
          <DialogTitle>Test report — audited deals re-run against this draft</DialogTitle>
          <DialogDescription>Each audited deal is evaluated under the draft with the shared evaluator and checked against its expected outputs (pass / fail); below, deals whose tier, stops, flags, offer, verdict or consolidation change versus Live.</DialogDescription>
        </DialogHeader>
        {err && <div className="text-sm text-rose-700" data-testid="policy-test-report-error">{err}</div>}
        {!r && !err && <div className="text-sm text-slate-500">{data ? "No test report yet — run the test set first." : "Loading…"}</div>}
        {r && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Stat label="Cases" value={r.cases_total} testId="policy-report-cases" />
              {r.cases_passed != null && <Stat label="Passed vs expected" value={`${r.cases_passed} / ${r.cases_total}`} tone={r.cases_failed?.length ? "text-rose-700" : "text-emerald-700"} testId="policy-report-passed" />}
              <Stat label="Deals changed" value={r.deals_changed?.length ?? 0} tone={r.deals_changed?.length ? "text-amber-700" : "text-emerald-700"} testId="policy-report-changed" />
              <Stat label="Unchanged" value={r.deals_unchanged} testId="policy-report-unchanged" />
              <Stat label="Warnings" value={r.warnings?.length ?? 0} tone={r.warnings?.length ? "text-amber-700" : ""} />
              {data?.stale && <Badge variant="outline" className="self-center bg-amber-50 text-amber-800 border-amber-300" data-testid="policy-report-stale">Stale — draft changed since this run</Badge>}
            </div>
            <div className="text-xs text-slate-500">Ran {fmtDate(r.ran_at)} by {r.ran_by} · vs Live {r.live_version_label} · {r.evaluator}{r.viewed_by?.length ? ` · viewed by ${r.viewed_by.join(", ")}` : ""}</div>
            <CaseResults cases={r.cases} />
            {r.deals_changed?.length ? (
              <div className="overflow-auto rounded-md border border-slate-200 max-h-[360px]">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]"><tr><th className="text-left p-2">Deal</th><th className="text-left p-2">Under Live</th><th className="text-left p-2">Under draft</th><th className="text-left p-2">Changed</th><th className="text-left p-2">Expected?</th></tr></thead>
                  <tbody>
                    {r.deals_changed.map((d) => (
                      <tr key={d.id} className="border-t border-slate-100 align-top" data-testid={`policy-report-deal-${d.id}`}>
                        <td className="p-2"><div className="font-medium">{d.name || d.id}</div><div className="text-slate-400">{d.sf} · {d.id}</div></td>
                        <td className="p-2"><Sig s={d.before} /></td><td className="p-2"><Sig s={d.after} /></td>
                        <td className="p-2">{(d.changed_fields || []).join(", ")}</td>
                        <td className="p-2">{d.matches_expected_after ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">matches</Badge> : <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100">differs</Badge>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <Empty testId="policy-report-no-changes">No audited deal changes outcome under this draft.</Empty>}
            {r.warnings?.length > 0 && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">{r.warnings.map((w, i) => <div key={i}>{w}</div>)}</div>}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="policy-test-report-close">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
